import type { GeoSdkController, FakeSdkQueueEntry, QueueSnapshot } from './geo-fake-sdk-controller'
import { getCookie } from '../../CookieStorageService'
import { subdivisionToCountry } from '../geo-subdivision-utils'
import { getSpoofSubdivision, getSpoofGuidanceContent } from '../geo-spoof-helpers'
import { getFaultInjector } from '../geo-fault-injector'

const GEOCOMPLY_SPOOF_SEQUENCE_COOKIE = 'GEOCOMPLY_SPOOF_SEQUENCE'

function readGuidanceContent(): Record<string, LocationGuidance> {
	const keys = ['terminal', 'retryable', 'hint']
	const result: Record<string, LocationGuidance> = {}
	for (const key of keys) {
		const content = getSpoofGuidanceContent(key)
		if (content) result[key] = content
	}
	return result
}

// readSequence reads directly from document.cookie — used by seed tests.
// GeoLocationService passes steps directly via seedQueueFromSteps for testability.
function readSequence(): string[] {
	const raw = getCookie(GEOCOMPLY_SPOOF_SEQUENCE_COOKIE)?.value
	if (!raw) return []
	try {
		return JSON.parse(decodeURIComponent(raw)) as string[]
	} catch {
		return []
	}
}

function makeSuccessResult(): LocateDeviceResult {
	const subdivision = getSpoofSubdivision() ?? 'US-TX'
	return {
		status: 'Success',
		subdivision,
		country: subdivisionToCountry(subdivision),
		token: '',
	}
}

// Reason values for PAY SDK failure categories. These are needed by the legacy
// geo-gate path (guidance OFF) which reads result.reason to determine the redirect URL.
// The real PAY SDK does not return reason, but the fake SDK must for accurate simulation.
const CATEGORY_TO_REASON: Partial<Record<FailureCategory, string>> = {
	permission: 'permission_denied',
	integrity: 'verification_failed',
}

function makeFailedResult(
	category: FailureCategory,
	guidanceContent: Record<string, LocationGuidance>,
	guidanceKey: string = 'terminal'
): LocateDeviceResult {
	const troubleshooting = guidanceContent[guidanceKey] ?? guidanceContent['terminal'] ?? { title: '', steps: [] }
	const reason = CATEGORY_TO_REASON[category]
	return {
		status: 'Failed',
		token: '',
		failureCategory: category,
		...(reason ? { reason } : {}),
		troubleshooting,
		faqUrl: (troubleshooting as LocationGuidance & { faqUrl?: string }).faqUrl,
	} as LocateDeviceResult
}

export function seedQueueFromSteps(steps: string[], push: (entry: FakeSdkQueueEntry) => void): void {
	if (steps.length === 0) return
	const guidanceContent = readGuidanceContent()
	seedSteps(steps, guidanceContent, push)
}

export function seedQueueFromCookies(push: (entry: FakeSdkQueueEntry) => void): void {
	const steps = readSequence()
	if (steps.length === 0) return
	const guidanceContent = readGuidanceContent()
	seedSteps(steps, guidanceContent, push)
}

function seedSteps(steps: string[], guidanceContent: Record<string, LocationGuidance>, push: (entry: FakeSdkQueueEntry) => void): void {

	for (const step of steps) {
		if (step === 'success' || step === 'our-retry:success' || step === 'sdk-retry:success') {
			push({ type: 'result', result: makeSuccessResult() })
		} else if (step === 'retryable:shown') {
			push({ type: 'result', result: makeFailedResult('integrity', guidanceContent, 'retryable'), opts: { retryable: true } })
		} else if (step === 'fail:permission') {
			push({ type: 'result', result: makeFailedResult('permission', guidanceContent) })
		} else if (step === 'fail:integrity') {
			push({ type: 'result', result: makeFailedResult('integrity', guidanceContent) })
		} else if (step === 'fail:error' || step === 'our-retry:fail' || step === 'sdk-retry:fail') {
			push({ type: 'result', result: makeFailedResult('error', guidanceContent) })
		} else if (step === 'fault:sdk_load_failed') {
			getFaultInjector()?.queueSdkLoadFault()
		} else if (step === 'fault:sdk_load_timeout') {
			getFaultInjector()?.queueSdkLoadTimeoutFault()
		} else if (step === 'fault:sdk_load_token_failed') {
			getFaultInjector()?.queueSdkLoadTokenFault()
		} else if (step === 'hint:shown') {
			const hintGuidance = guidanceContent['hint'] ?? guidanceContent['retryable'] ?? { title: '', steps: [] }
			push({ type: 'hint', guidance: hintGuidance })
		}
		// hint:skipped — discarded, no entry produced
	}
}

let nextEntryId = 0

function labelForEntry(entry: FakeSdkQueueEntry): string {
	if (entry.type === 'hint') return `Hint: ${entry.guidance.title || 'pre-hint'}`
	const result = entry.result
	if (result.status === 'Success') return `Success / ${(result as Extract<LocateDeviceResult, { status: 'Success' }>).subdivision}`
	const failed = result as Extract<LocateDeviceResult, { status: 'Failed' }>
	const category = failed.failureCategory ?? 'error'
	const retryable = entry.opts?.retryable ? ' (retryable)' : ''
	return `Failed / ${category}${retryable}`
}

export function createFakeSdk(): {
	locateDevice: (params: LocateDeviceParams) => Promise<LocateDeviceResult>
	controller: GeoSdkController
} {
	interface InternalEntry { id: number; entry: FakeSdkQueueEntry; consumed: boolean }
	const allEntries: InternalEntry[] = []
	const queue: FakeSdkQueueEntry[] = []
	const queueListeners = new Set<() => void>()
	let stopped = false
	let loginComplete = false
	let isMonitoringTick = false  // true when locateDevice is called from triggerTick
	let isProcessingTick = false  // true while a triggerTick locateDevice call is in flight
	let postHintGuidance: LocationGuidance | null = null
	let cachedParams: LocateDeviceParams | null = null

	function notifyQueue(): void {
		queueListeners.forEach(fn => fn())
	}

	async function locateDevice(params: LocateDeviceParams): Promise<LocateDeviceResult> {
		cachedParams = params
		if (stopped) return undefined as unknown as LocateDeviceResult
		// Capture and clear isMonitoringTick before any throw so a failed call
		// (empty queue, stopped) never leaves the flag stuck.
		const isTick = isMonitoringTick
		isMonitoringTick = false
		if (queue.length === 0) throw new Error('GeoFakeSDK: no result queued')
		// Real SDK resets isFirstLoop on each new locateDevice call (login or retry).
		// Monitoring ticks are continuations of the existing loop; do not reset.
		if (!isTick) loginComplete = false

		// inner loop — handles retryable and hint entries
		while (true) {
			if (queue.length === 0) throw new Error('GeoFakeSDK: no result queued')
			const entry = queue.shift()!
			const internalEntry = allEntries.find(e => e.entry === entry)
			if (internalEntry) { internalEntry.consumed = true; notifyQueue() }
			if (entry.type === 'hint') {
				if (params.showPreHint) await params.showPreHint(entry.guidance)
				postHintGuidance = entry.guidance
				continue
			}
			let result = entry.result
			if (postHintGuidance !== null) {
				if (result.status === 'Failed' && result.failureCategory === 'permission') {
					result = { ...result, troubleshooting: postHintGuidance }
				}
				postHintGuidance = null
			}
			if (
				entry.opts?.retryable &&
				params.showRetryableFailure &&
				result.status === 'Failed' &&
				result.failureCategory === 'integrity'
			) {
				const guidance = {
					...(result as Extract<LocateDeviceResult, { status: 'Failed' }>).troubleshooting,
					faqUrl: (result as Extract<LocateDeviceResult, { status: 'Failed' }>).faqUrl,
				}
				try {
					await params.showRetryableFailure(guidance as LocationGuidance & { faqUrl: string })
					continue
				} catch {
					return deliver(params, result, isTick)
				}
			}
			return deliver(params, result, isTick)
		}
	}

	function deliver(params: LocateDeviceParams, result: LocateDeviceResult, isTick: boolean): LocateDeviceResult {
		if (!loginComplete) {
			// Only a genuine new session (login or retry) activates the loop.
			// Monitoring ticks must not reactivate a dead loop.
			if (!isTick) loginComplete = true
			return result
		}
		params.callback(result)
		// Real SDK calls stopLocatingDevice() after a failure. Loop is dead until a new
		// locateDevice call (retry) restarts it.
		if (result.status === 'Failed') loginComplete = false
		return result
	}

	const controller: GeoSdkController = {
		queueResult(result, opts) {
			const entry: FakeSdkQueueEntry = { type: 'result', result, opts }
			queue.push(entry)
			allEntries.push({ id: ++nextEntryId, entry, consumed: false })
			notifyQueue()
		},
		queueHint(guidance) {
			const entry: FakeSdkQueueEntry = { type: 'hint', guidance }
			queue.push(entry)
			allEntries.push({ id: ++nextEntryId, entry, consumed: false })
			notifyQueue()
		},
		triggerTick() {
			if (stopped || !cachedParams || !loginComplete || isProcessingTick) return
			isMonitoringTick = true
			isProcessingTick = true
			void locateDevice(cachedParams).catch(() => {}).finally(() => { isProcessingTick = false })
		},
		reset() {
			queue.length = 0
			allEntries.length = 0
			stopped = false
			loginComplete = true  // player is still logged in; restore so triggerTick works after reset
			isMonitoringTick = false
			isProcessingTick = false
			postHintGuidance = null
			notifyQueue()
		},
		stop() { stopped = true },
		getQueueSnapshot(): QueueSnapshot[] {
			const firstPendingIndex = allEntries.findIndex(e => !e.consumed)
			return allEntries.map((internal, index) => ({
				id: internal.id,
				label: labelForEntry(internal.entry),
				status: internal.consumed
					? 'consumed'
					: index === firstPendingIndex
						? 'next'
						: 'pending',
			}))
		},
		subscribeToQueue(listener: () => void): () => void {
			queueListeners.add(listener)
			return () => queueListeners.delete(listener)
		},
	}

	return { locateDevice, controller }
}
